package com.arena_web.ui.components.chat

import androidx.compose.runtime.*
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.bodyLMedium
import com.arena_web.ui.theme.bodyLRegular
import com.arena_web.ui.theme.bodyMRegular
import com.arena_web.ui.theme.h3
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.TextInput
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.url.URL
import org.w3c.files.FileReader
import org.w3c.files.get

// Interactive/Secondary glass fill over an opaque body + the primary button
// hairline — the shared field recipe used across the chat sheets/popups.
private val FieldBackground =
    "${AppGradients.InteractiveSecondary} padding-box, " +
        "linear-gradient(0deg, #02040B, #02040B) padding-box, " +
        "${AppGradients.ButtonBorderGradientPrimary} border-box"

/**
 * Shared "Edit Community" form — camera/avatar picker + name field + Cancel/Save.
 * Drawn with `s()` helpers so it renders correctly inside both [com.arena_web.ui
 * .components.overlay.DesktopPopup] (global `--scale`) and a mobile [com.arena_web
 * .ui.components.overlay.BottomSheet] (`--scale: var(--mobile-scale)`).
 */
@Composable
fun EditChatContent(
    initialName: String,
    currentImageUrl: String?,
    isSaving: Boolean,
    onSave: (name: String, imageBytes: ByteArray?, imageFileName: String?, imageContentType: String?) -> Unit,
    onCancel: () -> Unit,
) {
    val t = LocalAppStrings.current
    var name by remember { mutableStateOf(initialName) }
    var imageBytes by remember { mutableStateOf<ByteArray?>(null) }
    var imageFileName by remember { mutableStateOf<String?>(null) }
    var imageContentType by remember { mutableStateOf<String?>(null) }
    var imagePreviewUrl by remember { mutableStateOf<String?>(null) }

    val pickImage = {
        val input = document.createElement("input") as HTMLInputElement
        input.type = "file"
        input.accept = "image/*"
        input.onchange = { _ ->
            val file = input.files?.get(0)
            if (file != null) {
                imagePreviewUrl = URL.createObjectURL(file)
                imageFileName = file.name
                imageContentType = file.type
                val reader = FileReader()
                reader.onload = {
                    val arrayBuffer = reader.result.unsafeCast<org.khronos.webgl.ArrayBuffer>()
                    val int8Array = org.khronos.webgl.Int8Array(buffer = arrayBuffer)
                    imageBytes = int8Array.unsafeCast<ByteArray>()
                }
                reader.readAsArrayBuffer(file)
            }
        }
        input.click()
    }

    Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(28)) }) {
        SpanText(t.chatEditCommunityTitle, modifier = Modifier.h3().color(AppColors.Text.Secondary))

        Column(
            modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(28)) },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Avatar — shows the picked preview, else the current group image,
            // else the camera glyph.
            val shownImage = imagePreviewUrl ?: currentImageUrl
            Box(
                modifier = Modifier
                    .sSize(80)
                    .sBorderRadius(100)
                    .cursor(Cursor.Pointer)
                    .onClick { pickImage() }
                    .styleModifier {
                        property("background", "rgba(0, 36, 93, 0.8)")
                        property("flex-shrink", "0")
                        property("overflow", "hidden")
                    },
                contentAlignment = Alignment.Center
            ) {
                if (shownImage != null) {
                    Image(src = shownImage, modifier = Modifier.fillMaxSize().objectFit(ObjectFit.Cover))
                } else {
                    Image(src = AppIcons.Camera, modifier = Modifier.sSize(24))
                }
            }

            // Name field.
            Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(6)) }) {
                SpanText(
                    t.chatCommunityNameLabel,
                    modifier = Modifier
                        .bodyMRegular()
                        .color(Color("#818d9c"))
                        .styleModifier { property("padding-left", s(4)) }
                )
                Row(
                    modifier = Modifier.fillMaxWidth().styleModifier {
                        property("height", s(44))
                        property("border-radius", s(28))
                        property("padding", "0 ${s(8)} 0 ${s(12)}")
                        property("border", "0.5px solid transparent")
                        property("background", FieldBackground)
                        property("box-sizing", "border-box")
                    },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    TextInput(
                        value = name,
                        attrs = Modifier
                            .fillMaxWidth()
                            .bodyLRegular()
                            .color(AppColors.Text.Primary)
                            .styleModifier {
                                property("background", "transparent")
                                property("border", "none")
                                property("outline", "none")
                                property("min-width", "0")
                                property("font-family", "Urbanist, sans-serif")
                            }
                            .toAttrs {
                                attr("placeholder", t.chatCommunityNamePlaceholder)
                                onInput { e -> name = e.target.asDynamic().value as String }
                            }
                    )
                }
            }
        }

        // Action buttons.
        Row(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(12)) }) {
            Box(
                modifier = Modifier
                    .styleModifier {
                        property("flex", "1")
                        property("height", s(44))
                        property("border-radius", s(100))
                        property("border", "0.3px solid transparent")
                        property("background", FieldBackground)
                        property("box-sizing", "border-box")
                    }
                    .cursor(Cursor.Pointer)
                    .onClick { onCancel() },
                contentAlignment = Alignment.Center
            ) {
                SpanText(t.commonCancel, modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary))
            }

            val canSave = name.isNotBlank() && !isSaving
            Box(
                modifier = Modifier
                    .styleModifier {
                        property("flex", "1")
                        property("height", s(44))
                        property("border-radius", s(100))
                        property("background", "#0061ff")
                        property("opacity", if (name.isNotBlank()) "1" else "0.5")
                    }
                    .cursor(if (canSave) Cursor.Pointer else Cursor.Default)
                    .onClick {
                        if (canSave) onSave(name.trim(), imageBytes, imageFileName, imageContentType)
                    },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    if (isSaving) t.chatSaving else t.commonSave,
                    modifier = Modifier.bodyLMedium().color(AppColors.PrimaryWhite)
                )
            }
        }
    }
}
